import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import essenza from "../../assets/beauty/essenza-ejemplo.png";
import vianca from "../../assets/beauty/vianca-ejemplo.png";
import maison from "../../assets/beauty/la-maison-ejemplo.png";
import muse from "../../assets/beauty/muse-ejemplo.png";

const CARD_GAP = 16;
const green = "#43BD98";

const staggerContainer = {
	hidden: {},
	show: { transition: { staggerChildren: 0.12 } },
};

const cardItem = {
	hidden: { opacity: 0, y: 30, scale: 0.97 },
	show: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] },
	},
};

const examples = [
	{
		id: "essenza",
		name: "Essenza",
		img: essenza,
		web: "https://web.applaby.com/essenza",
		ios: "https://apps.apple.com/es/app/essenza/id6745752145",
		android: "https://play.google.com/store/apps/details?id=com.applaby.essenza",
	},
	{
		id: "vianca",
		name: "Vianca",
		img: vianca,
		web: "https://web.applaby.com/vianca",
		ios: "https://apps.apple.com/es/app/vianca/id6744869023",
		android: "https://play.google.com/store/apps/details?id=com.applaby.vianca",
	},
	{
		id: "maison",
		name: "La Maison",
		img: maison,
		web: "https://www.lamaisonstudio.es",
	},
	{
		id: "muse",
		name: "Muse",
		img: muse,
		web: "https://www.musesalon.es",
	},
];

export default function Showcase2() {
	const { t } = useTranslation();
	const accent = "#D9B8CE";
	const isMobile = useMediaQuery("(max-width: 640px)");
	const trackRef = useRef(null);
	const [active, setActive] = useState(0);

	const stepWidth = () => {
		const track = trackRef.current;
		const first = track?.firstElementChild;
		return first ? first.offsetWidth + CARD_GAP : 0;
	};

	const handleScroll = useCallback(() => {
		const track = trackRef.current;
		const step = stepWidth();
		if (!track || !step) return;
		setActive(Math.round(track.scrollLeft / step));
	}, []);

	const goTo = (index) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({ left: index * stepWidth(), behavior: "smooth" });
	};

	return (
		<section
			id="ejemplos"
			className="py-20 bg-white text-gray-900 overflow-hidden"
			style={{ fontFamily: "'Poppins', sans-serif" }}
		>
			<div className="max-w-7xl mx-auto px-6 lg:px-16">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<p
						className="uppercase tracking-wider text-sm font-semibold mb-3"
						style={{ color: accent }}
					>
						{t("salon2.showcase.badge")}
					</p>
					<h2
						className="mb-4"
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
							fontWeight: 800,
							color: "#222",
						}}
					>
						{t("salon2.showcase.title")}
					</h2>
					<p
						className="max-w-2xl mx-auto"
						style={{ color: "#666", fontSize: "1.05rem", lineHeight: 1.7 }}
					>
						{t("salon2.showcase.subtitle")}
					</p>
				</motion.div>

				<motion.div
					ref={trackRef}
					onScroll={isMobile ? handleScroll : undefined}
					variants={isMobile ? undefined : staggerContainer}
					initial={isMobile ? false : "hidden"}
					whileInView={isMobile ? undefined : "show"}
					viewport={{ once: true, amount: 0.15 }}
					className={isMobile ? "hide-scrollbar" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"}
					style={
						isMobile
							? {
									display: "flex",
									gap: `${CARD_GAP}px`,
									overflowX: "auto",
									scrollSnapType: "x mandatory",
									marginInline: "-1.5rem",
									paddingInline: "1.5rem",
									scrollPaddingInline: "1.5rem",
									WebkitOverflowScrolling: "touch",
							  }
							: undefined
					}
				>
					{examples.map((salon) => (
						<motion.div
							key={salon.id}
							variants={isMobile ? undefined : cardItem}
							initial={isMobile ? { opacity: 0, y: 20 } : undefined}
							animate={isMobile ? { opacity: 1, y: 0 } : undefined}
							whileHover={
								isMobile
									? undefined
									: {
											y: -6,
											scale: 1.02,
											boxShadow: "0 20px 50px rgba(0,0,0,0.1)",
											transition: { duration: 0.3 },
									  }
							}
							className="group flex flex-col rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300"
							style={
								isMobile
									? {
											flex: "0 0 78%",
											scrollSnapAlign: "start",
									  }
									: undefined
							}
						>
							<a
								href={salon.web}
								target="_blank"
								rel="noreferrer"
								className="block relative overflow-hidden"
							>
								<img
									src={salon.img}
									alt={salon.name}
									className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
								/>
							</a>
							<div className="p-4 flex items-center justify-between gap-3">
								<a
									href={salon.web}
									target="_blank"
									rel="noreferrer"
									className="flex items-center justify-between flex-1 min-w-0"
								>
									<h3
										className="font-semibold text-base"
										style={{ color: "#222" }}
									>
										{salon.name}
									</h3>
									<ArrowUpRight
										size={18}
										className="text-gray-400 group-hover:text-gray-700 transition-colors shrink-0"
									/>
								</a>
							</div>
							{(salon.ios || salon.android) && (
								<div className="px-4 pb-4 -mt-1 flex items-center gap-2 text-xs text-gray-500">
									{salon.ios && (
										<a
											href={salon.ios}
											target="_blank"
											rel="noreferrer"
											className="hover:text-gray-800 transition-colors"
										>
											{t("salon2.showcase.ios")}
										</a>
									)}
									{salon.ios && salon.android && (
										<span className="text-gray-300">·</span>
									)}
									{salon.android && (
										<a
											href={salon.android}
											target="_blank"
											rel="noreferrer"
											className="hover:text-gray-800 transition-colors"
										>
											{t("salon2.showcase.android")}
										</a>
									)}
								</div>
							)}
						</motion.div>
					))}
				</motion.div>

				{isMobile && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: "8px",
							marginTop: "1.25rem",
						}}
					>
						{examples.map((salon, index) => (
							<button
								key={salon.id}
								type="button"
								onClick={() => goTo(index)}
								aria-label={salon.name}
								aria-current={index === active}
								style={{
									width: "24px",
									height: "24px",
									padding: 0,
									border: "none",
									background: "none",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<span
									style={{
										display: "block",
										width: index === active ? "24px" : "8px",
										height: "8px",
										borderRadius: "4px",
										background: index === active ? green : "#d1d5db",
										transition: "width 0.25s ease, background 0.25s ease",
									}}
								/>
							</button>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
